import { useId, type CSSProperties, type RefObject } from 'react'
import type { LuckyWheelItem } from '@/lib/buildLuckyWheelItems'
import type { CampaignBrandPalette } from '@/lib/campaignBrandPalette'
import { compactSliceLabel, polarPoint, prizeLabel, slicePath } from './rouletteGeometry'
import { RouletteIcon } from './RouletteIcon'

type Props = {
  items: LuckyWheelItem[]
  palette: CampaignBrandPalette
  rotorRef: RefObject<SVGGElement | null>
  pointerRef: RefObject<SVGGElement | null>
  selectedPosition?: number
  spinning: boolean
}

export function RouletteWheelFace({
  items,
  palette,
  rotorRef,
  pointerRef,
  selectedPosition,
  spinning,
}: Props) {
  const id = useId().replace(/:/g, '')
  const count = items.length
  const dense = count > 10
  const labelWidth = dense
    ? 104
    : Math.min(125, 2 * 128 * Math.sin(Math.PI / count) * 0.88)
  return (
    <svg
      className="roulette-face"
      viewBox="0 0 500 500"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={`${id}-rim`} x1="0" y1="0" x2="1" y2="1">
          <stop stopColor={palette.light} />
          <stop offset=".32" stopColor={palette.primary} />
          <stop offset=".7" stopColor={palette.deep} />
          <stop offset="1" stopColor={palette.accent} />
        </linearGradient>
        <linearGradient id={`${id}-pin`} x1="0" y1="0" x2=".8" y2="1">
          <stop stopColor="#fff" />
          <stop offset=".55" stopColor="#fff9e9" />
          <stop offset="1" stopColor={palette.light} />
        </linearGradient>
        <radialGradient id={`${id}-shade`}>
          <stop offset=".66" stopColor={palette.deep} stopOpacity="0" />
          <stop offset="1" stopColor={palette.deep} stopOpacity=".28" />
        </radialGradient>
        <filter id={`${id}-shadow`} x="-50%" y="-30%" width="200%" height="180%">
          <feDropShadow dx="0" dy="4" stdDeviation="3" floodOpacity=".25" />
        </filter>
        <clipPath id={`${id}-clip`}>
          <circle cx="250" cy="250" r="210" />
        </clipPath>
      </defs>
      <circle cx="250" cy="254" r="239" fill={palette.deep} opacity=".13" />
      <circle cx="250" cy="250" r="239" fill={`url(#${id}-rim)`} />
      <circle cx="250" cy="250" r="234" fill="none" stroke="#fff" strokeOpacity=".38" />
      <circle cx="250" cy="250" r="216" fill={palette.deep} />
      <g ref={rotorRef} className="roulette-rotor" clipPath={`url(#${id}-clip)`}>
        {items.map((item, index) => {
          const angle = ((index + 0.5) * 360) / count
          const light = index % 2 === 0
          const label = prizeLabel(item)
          const value = compactSliceLabel(label.value, count)
          const fontSize = Math.max(
            10.5,
            Math.min(dense ? 18 : 22, labelWidth / (Array.from(value).length * 0.59))
          )
          const selected = selectedPosition === index + 1
          return (
            <g
              key={item.hash}
              className={
                selected ? 'roulette-sector roulette-sector--selected' : 'roulette-sector'
              }
            >
              <path
                d={slicePath(index, count)}
                fill={light ? palette.muteBg : palette.sliceB}
              />
              <path
                d={slicePath(index, count)}
                fill="none"
                stroke={light ? palette.primary : '#fff'}
                strokeOpacity=".13"
                strokeWidth="1"
              />
              {selected && (
                <path
                  className="roulette-winning-sector"
                  d={slicePath(index, count)}
                  fill={palette.accent}
                  fillOpacity=".2"
                  stroke="#fff"
                  strokeWidth="3"
                />
              )}
              <g
                transform={`rotate(${angle} 250 250)`}
                color={light ? palette.deep : '#ffffff'}
              >
                {dense && (
                  <RouletteIcon
                    kind={item.premio?.tipo ?? 'none'}
                    x="241"
                    y="53"
                    width="18"
                    height="18"
                    opacity=".88"
                  />
                )}
                <g
                  transform={
                    dense
                      ? `translate(250 122) rotate(${angle > 180 ? 90 : -90})`
                      : `translate(250 108) rotate(${count <= 4 ? -angle : angle > 90 && angle < 270 ? 180 : 0})`
                  }
                >
                  {!dense && (
                    <RouletteIcon
                      kind={item.premio?.tipo ?? 'none'}
                      x="-12"
                      y="-37"
                      width="24"
                      height="24"
                      opacity=".88"
                    />
                  )}
                  <text
                    textAnchor="middle"
                    y={dense && label.type ? '0' : '7'}
                    fontSize={fontSize}
                    fontWeight="800"
                    fill="currentColor"
                    letterSpacing="-.5"
                    textLength={Math.min(
                      labelWidth,
                      Array.from(value).length * fontSize * 0.59
                    )}
                    lengthAdjust="spacingAndGlyphs"
                  >
                    {value}
                  </text>
                  {label.type && (
                    <text
                      textAnchor="middle"
                      y={dense ? '14' : '24'}
                      fontSize={dense ? 8 : 9.5}
                      fontWeight="600"
                      fill="currentColor"
                      opacity=".72"
                      letterSpacing=".8"
                    >
                      {label.type}
                    </text>
                  )}
                </g>
              </g>
            </g>
          )
        })}
        <circle
          cx="250"
          cy="250"
          r="210"
          fill={`url(#${id}-shade)`}
          pointerEvents="none"
        />
      </g>
      <circle
        cx="250"
        cy="250"
        r="211"
        fill="none"
        stroke="#fff"
        strokeOpacity=".55"
        strokeWidth="2"
      />
      <g
        className={
          spinning ? 'roulette-lamps roulette-lamps--spinning' : 'roulette-lamps'
        }
      >
        {Array.from({ length: 40 }, (_, index) => {
          const point = polarPoint(225, index * 9)
          return (
            <circle
              key={index}
              cx={point.x}
              cy={point.y}
              r="3"
              fill="#fff9de"
              className="roulette-lamp"
              style={{ '--lamp-index': index } as CSSProperties}
            />
          )
        })}
      </g>
      <g ref={pointerRef} className="roulette-pin" filter={`url(#${id}-shadow)`}>
        <path
          d="M250 64c-5-7-18-28-18-39a18 18 0 0 1 36 0c0 11-13 32-18 39Z"
          fill={`url(#${id}-pin)`}
          stroke="#fff"
          strokeWidth="2"
        />
        <circle cx="250" cy="24" r="6" fill={palette.deep} />
        <circle cx="248.5" cy="22.5" r="2" fill="#fff" opacity=".7" />
      </g>
    </svg>
  )
}
